//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'

// Import common DayJS locales
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/fr'
import 'dayjs/locale/pt-br'

import { theme } from '@react/components/theme'
import {
  assetsAndSharesToExchangeRate,
  dayNumberToDate,
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateMaturityDayCalc,
  exchangeRateToDecimalString,
  isNumber,
  toUTCDateString,
  weiToNumber,
} from '@shared/conversions'

import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtChainTimestamp, useOtDispatch, useOtSelector } from '@react/redux/store'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'

import OtAdminNumberField from '@react/components/OtAdminNumberField'
import { dynamicSetTermExchangeRateTx } from '@react/signedTxs/dynamicSetTermExchangeRate'
import { logOT } from '@shared/loggingShared'
import { EthAddr, NumberFormatEnum } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'

// Helper function to safely generate date format string
const getSafeDateFormatString = (locale: string): string => {
  try {
    const dateFormatOptions = Intl.DateTimeFormat(locale).formatToParts()
    const formatString = dateFormatOptions
      .map((part) => {
        switch (part.type) {
          case 'day':
            return 'DD'
          case 'month':
            return 'MM'
          case 'year':
            return 'YYYY'
          default:
            return part.value
        }
      })
      .join('')

    // Validate the format string is reasonable
    if (formatString && formatString.length > 2) {
      return formatString
    }
  } catch (error) {
    logOT.warn('Failed to generate date format for locale', locale, error)
  }

  // Fallback to standard format
  return 'DD/MM/YYYY'
}

// Helper function to get safe locale for DayJS
const getSafeLocale = (browserLocale: string): string => {
  const supportedLocales = ['en', 'pt-br', 'es', 'fr', 'de']
  const normalizedLocale = browserLocale.toLowerCase()

  // Direct match
  if (supportedLocales.includes(normalizedLocale)) {
    return normalizedLocale
  }

  // Try language code only (e.g., 'pt' from 'pt-BR')
  const languageCode = normalizedLocale.split('-')[0]
  if (supportedLocales.includes(languageCode)) {
    return languageCode
  }

  // Special case for Portuguese
  if (languageCode === 'pt') {
    return 'pt-br'
  }

  // Default fallback
  return 'en'
}

export default function DynamicSetTermExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)

  const chainTimestamp = useOtChainTimestamp()

  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )

  const [assetValue, setAssetValue] = React.useState<string>(erc20WeiToCommaDecString(poolCTO.totalAssets))

  const [numberOfDays, setNumberOfDays] = React.useState<number>(0)

  const [exchangeRateAtMaturity, setExchangeRateAtMaturity] = React.useState<string>(poolCTO.exchangeRateAtMaturity)

  // const originalAnnualLinearReturn = toAnnualTermReturn(
  //   exchangeRate,
  //   poolCTO.exchangeRateAtSetDay,
  //   poolCTO.exchangeRateAtMaturity,
  //   poolCTO.exchangeRateMaturityDay
  // )

  const [annualReturn, setAnnualReturn] = React.useState<string>(
    (poolCTO.interestRate ? Number(poolCTO.interestRate) / 100 : 0.0).toString()
  )

  const [exchangeRateMaturityDay, setExchangeRateMaturityDay] = React.useState<number>(
    poolCTO.exchangeRateMaturityDay > 0 ? poolCTO.exchangeRateMaturityDay : Math.floor(chainTimestamp / (24 * 60 * 60))
  )
  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  //  const [datePickerValue, setDatePickerValue] = React.useState<Dayjs>(dayjs(dayNumberToDate(exchangeRateMaturityDay)))

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const datePickerValue = dayjs(dayNumberToDate(exchangeRateMaturityDay)).isAfter(dayjs().utc().add(1, 'day'))
    ? dayjs(dayNumberToDate(exchangeRateMaturityDay))
    : dayjs().utc().add(1, 'day')
  const dispatch = useOtDispatch()
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  logOT.info(
    'poolCTO.exchangeRateMaturityDay  ',
    poolCTO.exchangeRateMaturityDay,
    Math.floor(chainTimestamp / (24 * 60 * 60)),
    exchangeRateMaturityDay
  )

  const setExchangeRateAction = (): void => {
    setIsSubmitted(true)

    dispatch(
      dynamicSetTermExchangeRateTx({
        poolAddr,
        exchangeRate,
        exchangeRateAtMaturity,
        exchangeRateMaturityDay,
        interestRate: Number(annualReturn) * 100,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false

  // Safe locale and date format handling
  const browserLocale = window.navigator?.language || 'en'
  const safeLocale = getSafeLocale(browserLocale)
  const dateFormatString = getSafeDateFormatString(browserLocale)

  const [isConfirmed, setIsConfirmed] = React.useState(false)

  logOT.info('Browser locale:', browserLocale, 'Safe locale:', safeLocale, 'Date format:', dateFormatString)

  const assetsAtMaturity = (BigInt(exchangeRateAtMaturity) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const newAssetsBI = (BigInt(exchangeRate) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const setDayAssets = (BigInt(poolCTO.exchangeRateAtSetDay) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  if (isConfirmed) {
    return (
      <Box sx={{ marginTop: 2, flexDirection: 'column', gap: 4 }}>
        <Typography variant="h4">Confirm Change Exchange Rate</Typography>

        <Typography variant="h4">
          Please confirm the change of exchange rate from {exchangeRateToDecimalString(poolCTO.exchangeRate)} to
          {exchangeRateToDecimalString(exchangeRate)}
        </Typography>
        <Typography variant="h4">
          Please new Exchange Rate At maturity {exchangeRateToDecimalString(exchangeRateAtMaturity)}
        </Typography>

        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangeRateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Box>
    )
  }
  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>At Last Exchange rate update</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(setDayAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRateAtSetDay)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Current values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Updated values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(newAssetsBI)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Values At Maturity</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(assetsAtMaturity)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(exchangeRateMaturityDay * 24 * 60 * 60)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(exchangeRateAtMaturity)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="assetValue"
          inputLabel="Updated Asset Value"
          value={assetValue}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.ERC20}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAssetValue(changedValue)
            const sanitizedValue = changedValue.replace(/,/g, '')
            const assetvalueNumber = Number(sanitizedValue)

            logOT.info('assetvalueNumber ', assetvalueNumber, changedValue)
            const er = assetsAndSharesToExchangeRate(assetvalueNumber, poolCTO.totalShares)
            setExchangeRateDecimalString(exchangeRateToDecimalString(er))
            setExchangeRate(er.toString())
            setExchangeRateAtMaturity(exchangeRateMaturityDayCalc(er, Number(annualReturn), numberOfDays).toString())
            // TODO            setExchangeRateChangeRate(annualReturnToExchangeRateChangeRate(annualLinearReturn, BigInt(exchangeRate)))
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="annualLinearReturn"
          inputLabel="Annual Linear Return"
          value={annualReturn}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.APR}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAnnualReturn(changedValue)
            if (isNumber(changedValue)) {
              const alr = Number(changedValue)

              setExchangeRateAtMaturity(exchangeRateMaturityDayCalc(exchangeRate, alr, numberOfDays).toString())
            }
          }}
        />

        <Box sx={{ float: 'right' }}>
          <Typography variant={'buttonBold'}>exchangeRateAtMaturity </Typography>
          <br />
          <Typography variant={'buttonBold'}>{exchangeRateAtMaturity} </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ marginTop: 4 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={safeLocale}>
          <DatePicker
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const v = newValue.format('YYYY-MM-DD ')
                const newDayNum = Math.floor(newValue.toDate().getTime() / (24 * 60 * 60 * 1000))
                logOT.info('newDayNum ', exchangeRateMaturityDay, ' change to ', newDayNum)
                setExchangeRateMaturityDay(newDayNum)
                const dayNum = Math.floor(chainTimestamp / (24 * 60 * 60))
                const _numberOfDays = newDayNum - dayNum
                setNumberOfDays(_numberOfDays)
                setExchangeRateAtMaturity(
                  exchangeRateMaturityDayCalc(exchangeRate, Number(annualReturn) / 100, _numberOfDays).toString()
                )
                logOT.info('v ', v)
              }
            }}
            minDate={dayjs().utc().add(1, 'day')}
            label="Maturity Date"
            format={dateFormatString}
            value={datePickerValue}
          />
        </LocalizationProvider>
        <Box sx={{ float: 'right' }}>
          <Typography variant={'buttonBold'}>Number of days </Typography>
          <br />
          <Typography variant={'buttonBold'}>{numberOfDays} </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Button
          disabled={disableButton}
          sx={sx}
          onClick={() => {
            setIsConfirmed(true)
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Confirm Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
          available in your vault ({weiToNumber(poolCTO?.totalAssets)}). Please deposit funds to the borrowerVault
        </Typography>
      )}
    </Grid>
  )
}
